import * as ZXing from "@zxing/library";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory } from "react-router-dom";
import useStyles from "./ReservationScannerStyles";
import { goToReservationResult, goToReservationsHome } from "../utils/reservationRoutes";
import useSpaceScan from "../hooks/useSpaceScan";
import useHidQrScanner from "../hooks/useHidQrScanner";

const ReservationScanner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { scanQr } = useSpaceScan();
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef(new ZXing.BrowserMultiFormatReader());
  const handledRef = useRef(false);
  const [cameraError, setCameraError] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // El QR leído se resuelve en el backend: él decide el status y qué evento registra.
  const handleDetected = useCallback(
    (qr: string) => {
      if (handledRef.current) return;
      handledRef.current = true;
      readerRef.current.reset();
      setValidating(true);
      scanQr(qr)
        .then((scan) => {
          goToReservationResult(history, scan);
        })
        .catch((err: Error) => {
          console.error(err);
          setValidating(false);
          setScanError(err.message);
        });
    },
    [history, scanQr]
  );

  // Lectora de QR (modo teclado): sirve igual que la cámara. `handleDetected` ya ignora un segundo
  // escaneo mientras hay uno en curso.
  useHidQrScanner({ enabled: !validating, onScan: handleDetected });

  useEffect(() => {
    const reader = readerRef.current;
    reader.timeBetweenDecodingAttempts = 300;
    handledRef.current = false;

    reader
      .decodeFromVideoDevice(null, videoRef.current, (result, err) => {
        if (result) {
          handleDetected(result.getText());
        }
        if (err && !(err instanceof ZXing.NotFoundException)) {
          // Errores de decodificación intermitentes son esperables; solo logueamos.
          console.error(err);
        }
      })
      .catch((err) => {
        console.error(err);
        setCameraError(true);
      });

    return () => {
      reader.reset();
    };
  }, [handleDetected, attempt]);

  const handleRetry = () => {
    setScanError(null);
    setCameraError(false);
    setAttempt((current) => current + 1);
  };

  const handleClose = () => {
    readerRef.current.reset();
    goToReservationsHome(history);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>Enfocá el QR</Typography>
        <Button className={classes.closeButton} onClick={handleClose} aria-label="Cerrar">
          <CloseIcon />
        </Button>
      </div>

      <div className={classes.viewport}>
        <video ref={videoRef} className={classes.video} muted playsInline />
        <div className={classes.scanLine} />
        <span className={`${classes.corner} ${classes.cornerTopLeft}`} />
        <span className={`${classes.corner} ${classes.cornerTopRight}`} />
        <span className={`${classes.corner} ${classes.cornerBottomLeft}`} />
        <span className={`${classes.corner} ${classes.cornerBottomRight}`} />

        {validating && (
          <div className={classes.fallback}>
            <CircularProgress size={40} />
            <Typography className={classes.fallbackText}>Validando reserva...</Typography>
          </div>
        )}

        {!validating && (cameraError || scanError) && (
          <div className={classes.fallback}>
            <Typography className={classes.fallbackText}>
              {scanError || "No pudimos acceder a la cámara."}
            </Typography>
            <Button className={classes.fallbackButton} onClick={handleRetry} disableElevation>
              Reintentar
            </Button>
          </div>
        )}
      </div>

      <Typography className={classes.footer}>
        La lectura es automática. No hace falta tocar nada.
      </Typography>
    </div>
  );
};

export default ReservationScanner;
